/**
 * ゲーム全体で共有するコンテキスト。
 * Phaser のシーンと DOM UI の両方がこれを参照する。
 */

import type { EventBus } from '../core/EventBus';
import type { AssistController } from '../meta/assist';
import type { GameStateMachine } from '../core/GameState';
import type { GameSettings } from './config/gameConfig';
import type { ScenePhase, StageDefinition, StageItemDefinition } from './config/types';
import type { AudioSystem } from './systems/AudioSystem';
import type { StorageSystem } from './systems/StorageSystem';
import type { IconProvider } from './utils/assetLoader';

export interface GameContext {
  /** 現在のステージ定義（コンテンツデータ）。 */
  stage: StageDefinition;
  /** レベル昇順のアイテム配列。 */
  items: StageItemDefinition[];
  /** レベル -> アイテム。 */
  itemByLevel: Map<number, StageItemDefinition>;
  maxLevel: number;
  bus: EventBus;
  state: GameStateMachine;
  storage: StorageSystem;
  audio: AudioSystem;
  icons: IconProvider;
  /** 実際に配置されている背景写真（時間帯 -> URL）。空なら手続き生成の夜景。 */
  sceneryPhotos: Map<ScenePhase, string>;
  /** 実際に配置されている背景ツアー写真（scene id -> URL）。空ならツアー無効。 */
  tourPhotos: Map<string, string>;
  /**
   * 「今日の県」で遊んでいるとき。全員が同じ順番で料理が落ちてくる。
   * ふつうのプレイでは undefined。
   */
  daily?: { dateKey: string; seed: string; shortDate: string };
  /** コンティニュー・おはし・広告・課金（Web版では enabled=false で何も出ない）。 */
  assist: AssistController;
  /** 変更が即時反映されるよう、同一オブジェクトを使い回す。 */
  settings: GameSettings;
  /** 読み込み中の幕へ進み具合（0〜1）を伝える。無ければ何もしない。 */
  reportLoadProgress?: (value: number) => void;
}

export function buildItemIndex(
  items: readonly StageItemDefinition[],
): Map<number, StageItemDefinition> {
  const map = new Map<number, StageItemDefinition>();
  items.forEach((item) => map.set(item.level, item));
  return map;
}
